import { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Users, Target, CheckCircle, XCircle, Clock, RefreshCw, Search, Instagram, Youtube, Phone, Mail, MapPin, ExternalLink, Loader2, User, MessageCircle, Plus, Download, Upload, FileSpreadsheet, Pencil, Info, Trash2, Link, UserPlus } from 'lucide-react';
import { AddReferralDialog } from '../../components/admin/AddReferralDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminCRM() {
  const [leads, setLeads] = useState([]);
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState(null);
  const [partnerStats, setPartnerStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', category: '', partnerType: '', activityStatus: '' });
  const [viewMode, setViewMode] = useState('leads'); // 'leads' or 'partners'
  const [leadsTab, setLeadsTab] = useState('all'); // 'all', 'today', 'overdue'
  const [todayCount, setTodayCount] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [partnerDialogOpen, setPartnerDialogOpen] = useState(false);
  const [addReferralPartner, setAddReferralPartner] = useState(null);
  const [addLeadDialogOpen, setAddLeadDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Partner filtering state
  const [sortBy, setSortBy] = useState('');
  const [timePeriod, setTimePeriod] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  
  // Edit lead state
  const [editLeadDialogOpen, setEditLeadDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  
  // URL analyzer state
  const [profileUrl, setProfileUrl] = useState('');
  const [analyzingProfile, setAnalyzingProfile] = useState(false);
  
  // Import results dialog state
  const [importResultsOpen, setImportResultsOpen] = useState(false);
  const [importResults, setImportResults] = useState(null);
  
  // Database search state
  const [dbSearchOpen, setDbSearchOpen] = useState(false);
  const [dbSearchQuery, setDbSearchQuery] = useState('');
  const [dbSearchResults, setDbSearchResults] = useState(null);
  const [dbSearching, setDbSearching] = useState(false);
  
  // Admin permissions state
  const [adminPermissions, setAdminPermissions] = useState({
    can_view: true,
    can_edit: true,
    can_delete: false,
    can_manage_admins: false,
    can_reset_passwords: false,
    can_download_code: false,
    can_purge_data: false,
    can_manage_payouts: true,
  });
  const [adminRole, setAdminRole] = useState('admin');
  
  // Load permissions from localStorage on mount
  useEffect(() => {
    const storedPermissions = localStorage.getItem('admin_permissions');
    const storedRole = localStorage.getItem('admin_role');
    if (storedPermissions) {
      try {
        setAdminPermissions(JSON.parse(storedPermissions));
      } catch (e) {
        console.error('Failed to parse permissions:', e);
      }
    }
    if (storedRole) {
      setAdminRole(storedRole);
    }
  }, []);

  const [newLead, setNewLead] = useState({
    name: '',
    instagram_handle: '',
    youtube_channel: '',
    email: '',
    phone: '',
    followers: '',
    category: '',
    city: '',
    notes: '',
    personality_types: [],
    // New fields
    description: '',
    remarks: '',
    partner_type: '',
    profession: '',
    years_of_experience: '',
    sample_status: ''
  });

  // Status options from PDF
  const statusOptions = [
    { value: 'new', label: 'New' },
    { value: 'in_follow_up', label: 'In Follow-Up' },
    { value: 'call_back_later', label: 'Call Back Later' },
    { value: 'did_not_pick_up', label: 'Did Not Pick Up' },
    { value: 'closed_referring', label: 'Closed & Referring' },
    { value: 'needs_reengagement', label: 'Needs Re-engagement' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'interested', label: 'Interested' },
    { value: 'negotiating', label: 'Negotiating' },
    { value: 'converted', label: 'Converted' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const partnerTypeOptions = [
    { value: 'individual', label: 'Individual' },
    { value: 'business', label: 'Business' }
  ];

  const sampleStatusOptions = [
    { value: 'products_to_be_sent', label: 'Products to be Sent' },
    { value: 'products_in_transit', label: 'Products In Transit' },
    { value: 'products_received', label: 'Products Received' },
    { value: 'products_received_feedback', label: 'Products Received & Feedback Taken' }
  ];

  const getAuthHeaders = () => {
    const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
    return sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
  };

  // XHR-based API request to bypass Emergent platform script interception
  const apiRequest = (method, url, data = null, timeout = 60000) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.timeout = timeout; // Set timeout (default 60 seconds)
      
      const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      if (sessionToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${sessionToken}`);
      }
      
      // Debug logging
      console.log(`[API Request] ${method} ${url}`);
      console.log(`[API Request] Token present: ${!!sessionToken}`);
      console.log(`[API Request] Token (first 20 chars): ${sessionToken ? sessionToken.substring(0, 20) : 'none'}...`);
      
      xhr.onload = function() {
        console.log(`[API Response] Status: ${xhr.status}`);
        console.log(`[API Response] Response length: ${xhr.responseText?.length || 0}`);
        try {
          const response = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ ok: true, status: xhr.status, data: response });
          } else {
            console.error(`[API Error] Status ${xhr.status}:`, response);
            resolve({ ok: false, status: xhr.status, data: response });
          }
        } catch (e) {
          console.error(`[API Error] Failed to parse response:`, xhr.responseText?.substring(0, 200));
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data: xhr.responseText });
        }
      };
      
      xhr.onerror = function() {
        console.error(`[API Error] Network error for ${method} ${url}`);
        console.error(`[API Error] readyState: ${xhr.readyState}, status: ${xhr.status}`);
        console.error(`[API Error] Response headers:`, xhr.getAllResponseHeaders());
        reject(new Error(`Network error - please check your connection. URL: ${url}`));
      };
      
      xhr.ontimeout = function() {
        console.error(`[API Error] Timeout after ${timeout}ms for ${method} ${url}`);
        reject(new Error('Request timed out - please try again'));
      };
      
      xhr.send(data ? JSON.stringify(data) : null);
    });
  };

  const downloadTemplate = async () => {
    try {
      const response = await fetch(`${API}/admin/crm/leads/template`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'crm_leads_template.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Template downloaded');
      } else {
        toast.error('Failed to download template');
      }
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      // Use XHR for file upload to bypass Emergent platform script interception
      const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API}/admin/crm/leads/upload-excel`, true);
        if (sessionToken) {
          xhr.setRequestHeader('Authorization', `Bearer ${sessionToken}`);
        }
        
        xhr.onload = function() {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve({ ok: xhr.status >= 200 && xhr.status < 300, data: response });
          } catch (e) {
            resolve({ ok: false, data: { detail: 'Invalid response' } });
          }
        };
        
        xhr.onerror = function() {
          reject(new Error('Network error'));
        };
        
        xhr.send(formData);
      });
      
      if (result.ok) {
        // Show detailed import results dialog
        setImportResults(result.data);
        setImportResultsOpen(true);
        fetchLeads();
        fetchStats();
      } else {
        toast.error(result.data?.detail || 'Failed to import leads');
      }
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const createLead = async () => {
    if (!newLead.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    try {
      const leadData = {
        ...newLead,
        followers: newLead.followers ? parseInt(newLead.followers.replace(/,/g, '')) : null,
        years_of_experience: newLead.years_of_experience !== '' && newLead.years_of_experience != null
          ? parseInt(newLead.years_of_experience)
          : null,
      };
      // Normalize empty strings on all optional string fields — backend Pydantic
      // treats "" the same as null for str fields, but not for int fields.
      Object.keys(leadData).forEach(k => {
        if (leadData[k] === '') leadData[k] = null;
      });
      
      // Use XHR to bypass Emergent platform script interception
      const response = await apiRequest('POST', `${API}/admin/crm/leads`, leadData);
      
      if (response.ok) {
        toast.success('Lead created successfully');
        setAddLeadDialogOpen(false);
        setNewLead({
          name: '',
          instagram_handle: '',
          youtube_channel: '',
          email: '',
          phone: '',
          followers: '',
          category: '',
          city: '',
          notes: '',
          personality_types: [],
          description: '',
          remarks: '',
          partner_type: '',
          profession: '',
          years_of_experience: '',
          sample_status: ''
        });
        fetchLeads();
        fetchStats();
      } else {
        toast.error(response.data?.detail || 'Failed to create lead');
      }
    } catch (error) {
      console.error('Create lead error:', error);
      toast.error('Failed to create lead: ' + error.message);
    }
  };

  const openEditDialog = (lead) => {
    setEditingLead({
      ...lead,
      followers: lead.followers ? lead.followers.toString() : '',
      years_of_experience: lead.years_of_experience ? lead.years_of_experience.toString() : ''
    });
    setEditLeadDialogOpen(true);
  };

  const handleEditLead = async () => {
    if (!editingLead) return;
    
    try {
      const updateData = {
        name: editingLead.name,
        instagram_handle: editingLead.instagram_handle,
        youtube_channel: editingLead.youtube_channel,
        email: editingLead.email,
        phone: editingLead.phone,
        followers: editingLead.followers ? parseInt(editingLead.followers.replace(/,/g, '')) : null,
        category: editingLead.category,
        city: editingLead.city,
        notes: editingLead.notes,
        personality_types: editingLead.personality_types,
        status: editingLead.status,
        follow_up_date: editingLead.follow_up_date,
        description: editingLead.description,
        remarks: editingLead.remarks,
        partner_type: editingLead.partner_type,
        profession: editingLead.profession,
        years_of_experience: editingLead.years_of_experience ? parseInt(editingLead.years_of_experience) : null,
        sample_status: editingLead.sample_status
      };
      
      // Use XHR to bypass Emergent platform script interception
      const response = await apiRequest('PUT', `${API}/admin/crm/leads/${editingLead.lead_id}`, updateData);
      
      if (response.ok) {
        toast.success('Lead updated successfully');
        setEditLeadDialogOpen(false);
        setEditingLead(null);
        fetchLeads();
        fetchStats();
      } else {
        toast.error(response.data?.detail || 'Failed to update lead');
      }
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const toggleEditLeadPersonality = (type) => {
    if (!editingLead) return;
    const current = editingLead.personality_types || [];
    if (current.includes(type)) {
      setEditingLead({ ...editingLead, personality_types: current.filter(t => t !== type) });
    } else if (current.length < 2) {
      setEditingLead({ ...editingLead, personality_types: [...current, type] });
    } else {
      toast.error('Maximum 2 personality types allowed');
    }
  };

  const toggleNewLeadPersonality = (type) => {
    const current = newLead.personality_types || [];
    if (current.includes(type)) {
      setNewLead({ ...newLead, personality_types: current.filter(t => t !== type) });
    } else if (current.length < 2) {
      setNewLead({ ...newLead, personality_types: [...current, type] });
    } else {
      toast.error('Maximum 2 personality types allowed');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/admin/crm/leads/stats`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const clearAllLeads = async () => {
    if (!window.confirm('⚠️ WARNING: This will DELETE ALL leads from the CRM. This cannot be undone. Are you sure?')) {
      return;
    }
    if (!window.confirm('🚨 FINAL CONFIRMATION: Delete ALL ' + (stats?.total || 0) + ' leads?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/admin/crm/leads/clear-all`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Deleted ${data.deleted} leads`);
        fetchLeads();
        fetchStats();
      } else {
        toast.error('Failed to clear leads');
      }
    } catch (error) {
      toast.error('Failed to clear leads');
    }
  };

  const fetchPartnerStats = async () => {
    try {
      const response = await fetch(`${API}/admin/crm/partners/stats`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setPartnerStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch partner stats:', error);
    }
  };

  const fetchPartners = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/crm/partners?page=${page}&limit=500`;
      if (filter.status) url += `&crm_status=${filter.status}`;
      if (filter.activityStatus) url += `&activity_status=${filter.activityStatus}`;
      if (sortBy) url += `&sort_by=${sortBy}`;
      if (timePeriod && timePeriod !== 'all') url += `&time_period=${timePeriod}`;
      
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });
      
      if (response.ok) {
        const data = await response.json();
        setPartners(data.partners || []);
        setTotalPages(data.pages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePartner = async (userId, updates, showToast = false, refetchList = false) => {
    try {
      const response = await fetch(`${API}/admin/crm/partners/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(updates),
      });
      
      if (response.ok) {
        if (showToast) {
          toast.success('Partner updated');
        }
        // Only refetch lists if explicitly requested
        if (refetchList) {
          fetchPartners();
          fetchPartnerStats();
        } else {
          // Just update the local partners array without refetching
          setPartners(prev => prev.map(partner => 
            partner.user_id === userId ? { ...partner, ...updates } : partner
          ));
        }
        // Update selected partner in dialog
        if (selectedPartner && selectedPartner.user_id === userId) {
          setSelectedPartner(prev => ({ ...prev, ...updates }));
        }
      } else {
        toast.error('Failed to update partner');
      }
    } catch (error) {
      toast.error('Failed to update partner');
    }
  };

  // Quick follow-up for at-risk partners
  const setQuickFollowUp = async (e, partner, days = 1) => {
    e.stopPropagation(); // Prevent row click
    const followUpDate = new Date();
    followUpDate.setDate(followUpDate.getDate() + days);
    const dateStr = followUpDate.toISOString().split('T')[0];
    
    await updatePartner(partner.user_id, { 
      crm_follow_up_date: dateStr,
      crm_status: 'follow_up'
    });
    toast.success(`Follow-up set for ${days === 1 ? 'tomorrow' : `${days} days`}`);
  };

  // Analyze profile URL with AI
  const analyzeProfile = async (url, leadId = null) => {
    if (!url.trim()) {
      toast.error('Please enter a profile URL');
      return;
    }
    
    setAnalyzingProfile(true);
    try {
      // Use XHR to bypass Emergent platform script interception
      const response = await apiRequest('POST', `${API}/admin/crm/analyze-profile`, { url, lead_id: leadId });
      
      if (response.ok) {
        toast.success('Profile analyzed! Notes updated.');
        // Refresh lead data if we have a lead ID
        if (leadId && selectedLead) {
          const updatedLead = await fetch(`${API}/admin/crm/leads/${leadId}`, {
            headers: getAuthHeaders(),
          }).then(r => r.json());
          setSelectedLead(updatedLead);
        }
        setProfileUrl('');
        return response.data.analysis;
      } else {
        toast.error(response.data?.detail || 'Failed to analyze profile');
      }
    } catch (error) {
      toast.error('Failed to analyze profile');
    } finally {
      setAnalyzingProfile(false);
    }
  };

  const fetchFollowUpCounts = async () => {
    try {
      const response = await fetch(`${API}/admin/crm/leads/follow-up-counts`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setTodayCount(data.today || 0);
        setOverdueCount(data.overdue || 0);
      }
    } catch (error) {
      console.error('Failed to fetch follow-up counts:', error);
    }
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/crm/leads?page=${page}&limit=50`;
      
      // Add tab-based filtering
      if (leadsTab === 'today') {
        url += '&follow_up=today';
      } else if (leadsTab === 'overdue') {
        url += '&follow_up=overdue';
      } else {
        // Apply regular filters only for "all" tab
        if (filter.status) url += `&status=${filter.status}`;
        if (filter.category) url += `&category=${filter.category}`;
        if (filter.partnerType) url += `&partner_type=${filter.partnerType}`;
      }
      
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });
      
      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
        setTotalPages(data.pages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateLead = async (leadId, updates, showToast = false, refetchList = false) => {
    try {
      const response = await fetch(`${API}/admin/crm/leads/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(updates),
      });
      
      if (response.ok) {
        if (showToast) {
          toast.success('Lead updated');
        }
        // Only refetch lists if explicitly requested (e.g., status change that affects counts)
        if (refetchList) {
          fetchLeads();
          fetchStats();
        } else {
          // Just update the local leads array without refetching
          setLeads(prev => prev.map(lead => 
            lead.lead_id === leadId ? { ...lead, ...updates } : lead
          ));
        }
        // Update selected lead in dialog
        if (selectedLead && selectedLead.lead_id === leadId) {
          setSelectedLead(prev => ({ ...prev, ...updates }));
        }
      } else {
        toast.error('Failed to update lead');
      }
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  useEffect(() => {
    // Always fetch both stats on mount for the toggle buttons
    fetchStats();
    fetchPartnerStats();
    fetchFollowUpCounts();
  }, []);

  useEffect(() => {
    if (viewMode === 'leads') {
      fetchLeads();
      fetchFollowUpCounts();
    } else {
      fetchPartners();
    }
  }, [filter, page, viewMode, sortBy, timePeriod, leadsTab]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'in_follow_up': return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'call_back_later': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'did_not_pick_up': return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'contacted': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'interested': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'negotiating': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'converted': return 'bg-green-100 text-green-700 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-700 border-red-200';
      case 'closed_referring': return 'bg-teal-100 text-teal-700 border-teal-200';
      case 'needs_reengagement': return 'bg-rose-100 text-rose-700 border-rose-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getPersonalityColor = (type) => {
    switch (type) {
      case 'power': return 'bg-red-100 text-red-700';
      case 'significance': return 'bg-purple-100 text-purple-700';
      case 'approval': return 'bg-green-100 text-green-700';
      case 'acceptance': return 'bg-blue-100 text-blue-700';
      case 'intelligence': return 'bg-amber-100 text-amber-700';
      case 'pity': return 'bg-pink-100 text-pink-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const PERSONALITY_TYPES = [
    { value: 'power', label: 'Power' },
    { value: 'significance', label: 'Significance' },
    { value: 'approval', label: 'Approval' },
    { value: 'acceptance', label: 'Acceptance' },
    { value: 'intelligence', label: 'Intelligence' },
    { value: 'pity', label: 'Pity' },
  ];

  const togglePersonalityType = (type) => {
    if (!selectedLead) return;
    const current = selectedLead.personality_types || [];
    let updated;
    if (current.includes(type)) {
      updated = current.filter(t => t !== type);
    } else if (current.length < 2) {
      updated = [...current, type];
    } else {
      toast.error('Maximum 2 personality types allowed');
      return;
    }
    updateLead(selectedLead.lead_id, { personality_types: updated });
  };

  const togglePartnerPersonalityType = (type) => {
    if (!selectedPartner) return;
    const current = selectedPartner.crm_personality_types || [];
    let updated;
    if (current.includes(type)) {
      updated = current.filter(t => t !== type);
    } else if (current.length < 2) {
      updated = [...current, type];
    } else {
      toast.error('Maximum 2 personality types allowed');
      return;
    }
    updatePartner(selectedPartner.user_id, { crm_personality_types: updated });
  };

  const getCRMStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'needs_attention': return 'bg-red-100 text-red-700';
      case 'follow_up': return 'bg-yellow-100 text-yellow-700';
      case 'inactive': return 'bg-gray-100 text-gray-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'nutritionist': return 'Nutritionist';
      case 'dietician': return 'Dietician';
      case 'doctor': return 'Doctor';
      case 'lifestyle_coach': return 'Lifestyle Coach';
      case 'influencer': return 'Influencer';
      case 'fitness_coach': return 'Fitness Coach';
      default: return category || 'Not Set';
    }
  };

  const formatFollowers = (num) => {
    if (!num) return 'N/A';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const filteredLeads = leads.filter(lead => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      lead.name?.toLowerCase().includes(query) ||
      lead.instagram_handle?.toLowerCase().includes(query) ||
      lead.city?.toLowerCase().includes(query)
    );
  });

  const filteredPartners = partners.filter(partner => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      partner.name?.toLowerCase().includes(query) ||
      partner.email?.toLowerCase().includes(query) ||
      partner.city?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Partner CRM
          </h1>
          <div className="flex items-center gap-2">
            <p className="text-muted-foreground">Manage leads and existing partners</p>
            <Badge 
              variant={adminRole === 'super_admin' ? 'default' : adminRole === 'admin' ? 'secondary' : 'outline'}
              className={adminRole === 'super_admin' ? 'bg-purple-600' : adminRole === 'viewer' ? 'bg-gray-400' : ''}
            >
              {adminRole === 'super_admin' ? 'Super Admin' : adminRole === 'viewer' ? 'Viewer' : 'Admin'}
            </Badge>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {viewMode === 'leads' && (
            <>
              {adminPermissions.can_edit && (
              <Button onClick={() => setAddLeadDialogOpen(true)} variant="default">
                <Plus className="h-4 w-4 mr-2" />
                Add Lead
              </Button>
              )}
              <Button onClick={downloadTemplate} variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Template
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".xlsx,.xls"
                className="hidden"
              />
              <Button 
                onClick={() => fileInputRef.current?.click()} 
                disabled={uploading}
                variant="outline"
              >
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                {uploading ? 'Importing...' : 'Import Excel'}
              </Button>
              {adminPermissions.can_delete && (
              <Button 
                onClick={clearAllLeads} 
                variant="outline"
                className="text-red-600 hover:text-red-700"
              >
                Clear All Leads
              </Button>
              )}
              <Button 
                onClick={async () => {
                  try {
                    toast.info('Sending CRM report...');
                    const response = await fetch(`${API}/admin/crm/daily-report/send`, {
                      method: 'POST',
                      headers: getAuthHeaders(),
                    });
                    if (response.ok) {
                      const data = await response.json();
                      toast.success(data.message || 'CRM Report sent to admins!');
                    } else {
                      const error = await response.json().catch(() => ({}));
                      toast.error(error.detail || `Failed to send report (${response.status})`);
                      console.error('CRM Report error:', error);
                    }
                  } catch (error) {
                    toast.error('Failed to send report: ' + error.message);
                    console.error('CRM Report error:', error);
                  }
                }}
                variant="outline"
                className="text-blue-600 hover:text-blue-700"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Send CRM Report
              </Button>
            </>
          )}
          {viewMode === 'partners' && (
            <Button 
              onClick={async () => {
                try {
                  let url = `${API}/admin/crm/partners/export?`;
                  if (sortBy) url += `sort_by=${sortBy}&`;
                  if (timePeriod && timePeriod !== 'all') url += `time_period=${timePeriod}&`;
                  if (filter.status) url += `crm_status=${filter.status}&`;
                  if (timePeriod === 'custom') {
                    if (customStart) url += `start_date=${new Date(customStart).toISOString()}&`;
                    if (customEnd) url += `end_date=${new Date(customEnd + 'T23:59:59').toISOString()}&`;
                  }
                  
                  toast.info('Preparing export...');
                  
                  // Get session token from storage
                  const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
                  
                  if (!sessionToken) {
                    toast.error('Not authenticated. Please login again.');
                    return;
                  }
                  
                  // Use XHR for file download to bypass platform script interception
                  const result = await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open('GET', url, true);
                    xhr.responseType = 'blob';
                    xhr.timeout = 60000; // 60 second timeout
                    xhr.setRequestHeader('Authorization', `Bearer ${sessionToken}`);
                    
                    xhr.onload = function() {
                      if (xhr.status >= 200 && xhr.status < 300) {
                        resolve({ ok: true, blob: xhr.response });
                      } else {
                        // Try to read error message from blob
                        if (xhr.response && xhr.response.size > 0 && xhr.response.size < 1000) {
                          const reader = new FileReader();
                          reader.onload = function() {
                            try {
                              const error = JSON.parse(reader.result);
                              resolve({ ok: false, status: xhr.status, error: error.detail || 'Export failed' });
                            } catch (e) {
                              resolve({ ok: false, status: xhr.status, error: 'Export failed' });
                            }
                          };
                          reader.readAsText(xhr.response);
                        } else {
                          resolve({ ok: false, status: xhr.status, error: 'Export failed' });
                        }
                      }
                    };
                    
                    xhr.onerror = function() {
                      reject(new Error('Network error'));
                    };
                    
                    xhr.ontimeout = function() {
                      reject(new Error('Request timed out'));
                    };
                    
                    xhr.send();
                  });
                  
                  if (result.ok) {
                    const sortLabel = sortBy ? `_${sortBy}` : '';
                    const periodLabel = timePeriod && timePeriod !== 'all' ? `_${timePeriod}` : '';
                    const filename = `KiloBeaters_Partners${sortLabel}${periodLabel}_${new Date().toISOString().slice(0,10)}.xlsx`;
                    
                    const downloadUrl = window.URL.createObjectURL(result.blob);
                    const link = document.createElement('a');
                    link.style.display = 'none';
                    link.href = downloadUrl;
                    link.download = filename;
                    link.setAttribute('download', filename);
                    document.body.appendChild(link);
                    link.click();
                    
                    setTimeout(() => {
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(downloadUrl);
                    }, 100);
                    
                    toast.success('Partners exported successfully');
                  } else {
                    toast.error(result.error || `Failed to export partners (${result.status})`);
                  }
                } catch (error) {
                  console.error('Export error:', error);
                  toast.error('Failed to export: ' + error.message);
                }
              }}
              variant="outline"
              data-testid="export-partners-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Excel
            </Button>
          )}
          <Button onClick={() => { 
            if (viewMode === 'leads') { fetchLeads(); fetchStats(); } 
            else { fetchPartners(); fetchPartnerStats(); } 
          }} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {adminPermissions.can_purge_data && (
          <Button 
            onClick={async () => {
              try {
                toast.info('Checking for orphaned data...');
                const response = await fetch(`${API}/admin/crm/orphaned-data-preview`, {
                  headers: getAuthHeaders()
                });
                const data = await response.json();
                if (response.ok) {
                  if (data.total_would_be_purged === 0) {
                    toast.success('No orphaned data found!');
                  } else {
                    const confirm = window.confirm(
                      `Found ${data.total_would_be_purged} orphaned records:\n` +
                      `- ${data.incomplete_users.length} incomplete users\n` +
                      `- ${data.duplicate_emails.length} duplicate emails\n\n` +
                      `Do you want to purge these records?`
                    );
                    if (confirm) {
                      const purgeResponse = await fetch(`${API}/admin/crm/purge-orphaned-data`, {
                        method: 'POST',
                        headers: getAuthHeaders()
                      });
                      const purgeData = await purgeResponse.json();
                      if (purgeResponse.ok) {
                        toast.success(purgeData.message);
                        fetchPartners();
                        fetchPartnerStats();
                      } else {
                        toast.error(purgeData.detail || 'Failed to purge');
                      }
                    }
                  }
                } else {
                  toast.error(data.detail || 'Failed to check orphaned data');
                }
              } catch (error) {
                toast.error('Failed: ' + error.message);
              }
            }}
            variant="outline"
            className="text-orange-600 hover:text-orange-700"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Purge Orphaned Data
          </Button>
          )}
          <Button 
            onClick={() => setDbSearchOpen(true)}
            variant="outline"
          >
            <Search className="h-4 w-4 mr-2" />
            Search Database
          </Button>
          {adminPermissions.can_download_code && (
          <Button 
            onClick={async () => {
              try {
                toast.info('Preparing project download...');
                const response = await fetch(`${API}/admin/download-project`, {
                  headers: getAuthHeaders()
                });
                if (response.ok) {
                  const blob = await response.blob();
                  const contentDisposition = response.headers.get('Content-Disposition');
                  let filename = 'kilobeaters_portal.zip';
                  if (contentDisposition) {
                    const match = contentDisposition.match(/filename=(.+)/);
                    if (match) filename = match[1];
                  }
                  const downloadUrl = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = downloadUrl;
                  link.download = filename;
                  document.body.appendChild(link);
                  link.click();
                  setTimeout(() => {
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(downloadUrl);
                  }, 100);
                  toast.success('Project downloaded successfully!');
                } else {
                  const error = await response.json().catch(() => ({}));
                  toast.error(error.detail || 'Failed to download project');
                }
              } catch (error) {
                toast.error('Download failed: ' + error.message);
              }
            }}
            variant="outline"
            className="text-purple-600 hover:text-purple-700 border-purple-300"
            data-testid="download-project-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Download Project
          </Button>
          )}
          <Button 
            onClick={async () => {
              const forceRecreate = window.confirm(
                'Do you want to FORCE RECREATE all discount codes?\n\n' +
                '• Click OK to delete and recreate ALL codes in Shopify (use if codes not working)\n' +
                '• Click Cancel to only create missing codes'
              );
              try {
                const syncUrl = forceRecreate 
                  ? `${API}/admin/shopify/sync-discount-codes?force_recreate=true`
                  : `${API}/admin/shopify/sync-discount-codes`;
                
                console.log('[Sync] Starting sync...');
                console.log('[Sync] URL:', syncUrl);
                console.log('[Sync] Force recreate:', forceRecreate);
                
                toast.info(forceRecreate 
                  ? 'Force recreating all discount codes... This may take several minutes for many partners.' 
                  : 'Syncing discount codes to Shopify... This may take a few minutes.');
                
                // Use 5 minute timeout for sync operation (100 partners = ~60 seconds minimum)
                const response = await apiRequest('POST', syncUrl, null, 300000);
                
                console.log('[Sync] Response:', response);
                
                if (response.ok) {
                  const data = response.data;
                  let message = `Sync complete!\n`;
                  message += `• Total partners: ${data.total}\n`;
                  message += `• Processed: ${data.processed || data.total}\n`;
                  if (data.recreated > 0) {
                    message += `• Codes recreated: ${data.recreated}\n`;
                  }
                  if (data.created > 0) {
                    message += `• New codes created: ${data.created}\n`;
                  }
                  message += `• Already synced: ${data.already_exists}\n`;
                  message += `• Links updated: ${data.links_updated}\n`;
                  if (data.failed > 0) {
                    message += `• Failed: ${data.failed}`;
                  }
                  toast.success(message);
                  if (data.errors && data.errors.length > 0) {
                    console.error('Sync errors:', data.errors);
                    toast.warning(`${data.errors.length} errors occurred. Check console for details.`);
                  }
                } else {
                  console.error('[Sync] Failed response:', response);
                  toast.error(response.data?.detail || `Failed to sync discount codes (${response.status})`);
                }
              } catch (error) {
                console.error('[Sync] Exception:', error);
                toast.error('Sync failed: ' + error.message);
              }
            }}
            variant="outline"
            className="text-green-600 hover:text-green-700 border-green-300"
            data-testid="sync-shopify-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Sync to Shopify
          </Button>
          {(adminRole === 'super_admin' || adminRole === 'admin') && (
            <Button 
              onClick={async () => {
                if (!confirm('This will update ALL partner referral links to the correct auto-apply format:\n\nkilobeaters-in.myshopify.com/discount/CODE\n\nContinue?')) return;
                
                try {
                  toast.info('Fixing referral links...');
                  const xhr = new XMLHttpRequest();
                  xhr.open('POST', `${API}/admin/partners/fix-referral-links`, true);
                  xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('session_token') || sessionStorage.getItem('session_token')}`);
                  xhr.onload = function() {
                    console.log('[Fix Links] Status:', xhr.status, 'Response:', xhr.responseText);
                    if (xhr.status >= 200 && xhr.status < 300) {
                      const response = JSON.parse(xhr.responseText);
                      toast.success(`Fixed ${response.updated} of ${response.total_partners} partner links`);
                    } else {
                      try {
                        const error = JSON.parse(xhr.responseText);
                        toast.error('Failed: ' + (error.detail || error.message || 'Unknown error'));
                        alert('Error details:\n\n' + JSON.stringify(error, null, 2));
                      } catch {
                        toast.error('Failed to fix referral links: ' + xhr.status);
                        alert('Raw error response:\n\n' + xhr.responseText);
                      }
                    }
                  };
                  xhr.onerror = function() {
                    toast.error('Network error - check console');
                    console.error('[Fix Links] Network error');
                  };
                  xhr.send();
                } catch (error) {
                  toast.error('Error: ' + error.message);
                  console.error('[Fix Links] Exception:', error);
                }
              }}
              variant="outline"
              className="text-purple-600 hover:text-purple-700 border-purple-300"
              data-testid="fix-referral-links-btn"
            >
              <Link className="h-4 w-4 mr-2" />
              Fix All Links
            </Button>
          )}
          {(adminRole === 'super_admin' || adminRole === 'admin') && (
            <Button 
              onClick={async () => {
                if (!confirm('Reset Super Admin password?\n\nThis will reset shyma@kilobeaters.com password to: Merman123\n\nContinue?')) return;
                
                try {
                  toast.info('Resetting Super Admin password...');
                  const xhr = new XMLHttpRequest();
                  xhr.open('POST', `${API}/auth/admin/setup-admins`, true);
                  xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('session_token') || sessionStorage.getItem('session_token')}`);
                  xhr.onload = function() {
                    if (xhr.status >= 200 && xhr.status < 300) {
                      toast.success('Admin passwords reset! Super Admin: shyma@kilobeaters.com / Merman123');
                      alert('Admin passwords reset!\n\nSuper Admin: shyma@kilobeaters.com\nPassword: Merman123');
                    } else {
                      toast.error('Failed to reset passwords');
                    }
                  };
                  xhr.onerror = function() {
                    toast.error('Network error');
                  };
                  xhr.send();
                } catch (error) {
                  toast.error('Error: ' + error.message);
                }
              }}
              variant="outline"
              className="text-red-600 hover:text-red-700 border-red-300"
              data-testid="reset-admin-btn"
            >
              Reset Admins
            </Button>
          )}
          <Button 
            onClick={async () => {
              try {
                toast.info('Testing API connectivity...');
                console.log('[Debug] API URL:', API);
                console.log('[Debug] Session token:', localStorage.getItem('session_token')?.substring(0, 30) || sessionStorage.getItem('session_token')?.substring(0, 30) || 'NONE');
                
                // Test 1: Basic health check
                const healthCheck = await fetch(`${API.replace('/api', '')}/`);
                const healthData = await healthCheck.json();
                console.log('[Debug] Health check:', healthData);
                
                // Test 2: Debug endpoint
                const debugResp = await fetch(`${API}/debug/info`);
                const debugData = await debugResp.json();
                console.log('[Debug] Debug info:', debugData);
                
                // Test 3: Auth check
                const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
                const authResp = await fetch(`${API}/auth/me`, {
                  headers: { 'Authorization': `Bearer ${sessionToken}` }
                });
                const authData = await authResp.json();
                console.log('[Debug] Auth check:', authData);
                
                toast.success(`API OK! Backend: ${debugData.app_url || 'unknown'}`);
                
                alert(`Debug Info:\n\nAPI URL: ${API}\nBackend App URL: ${debugData.app_url}\nShopify Domain: ${debugData.shopify_domain}\nAuth Status: ${authData.email || authData.detail || 'unknown'}\n\nCheck browser console for full details.`);
              } catch (error) {
                console.error('[Debug] Error:', error);
                toast.error('Debug failed: ' + error.message);
                alert(`Debug Error:\n\n${error.message}\n\nAPI URL: ${API}\n\nThis usually means:\n1. Backend is down\n2. Wrong API URL\n3. CORS blocking\n\nCheck browser console (F12) for details.`);
              }
            }}
            variant="outline"
            className="text-gray-500 hover:text-gray-700 border-gray-300"
            size="sm"
            data-testid="debug-api-btn"
          >
            🔍 Debug
          </Button>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="flex gap-2">
        <Button 
          variant={viewMode === 'leads' ? "default" : "outline"} 
          onClick={() => { setViewMode('leads'); setPage(1); setFilter({ status: '', category: '' }); }}
        >
          <Target className="h-4 w-4 mr-2" />
          Potential Leads ({stats?.total || 0})
        </Button>
        <Button 
          variant={viewMode === 'partners' ? "default" : "outline"} 
          onClick={() => { setViewMode('partners'); setPage(1); setFilter({ status: '', category: '' }); }}
        >
          <Users className="h-4 w-4 mr-2" />
          Existing Partners ({partnerStats?.total_partners || 0})
        </Button>
      </div>

      {/* Stats Cards - Leads */}
      {viewMode === 'leads' && stats && (
        <div className="grid gap-4 md:grid-cols-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Leads</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">New</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-500">{stats.by_status?.new || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Contacted</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-500">{stats.by_status?.contacted || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Interested</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-500">{stats.by_status?.interested || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Converted</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-500">{stats.by_status?.converted || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Follow-ups Today</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-500">{stats.follow_ups_due_today || 0}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stats Cards - Partners */}
      {viewMode === 'partners' && partnerStats && (
        <div className="grid gap-4 md:grid-cols-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Partners</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{partnerStats.total_partners || 0}</div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:bg-green-50 transition-colors" onClick={() => setFilter({...filter, activityStatus: 'has_transactions'})}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                Referrals Transacting
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{partnerStats.referrals_transacting || 0}</div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:bg-yellow-50 transition-colors" onClick={() => setFilter({...filter, activityStatus: 'added_referrals'})}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500"></span>
                Referrals Added
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{partnerStats.referrals_added || 0}</div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:bg-orange-50 transition-colors" onClick={() => setFilter({...filter, activityStatus: 'logged_in_only'})}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-orange-500"></span>
                No Referrals
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{partnerStats.no_referrals || 0}</div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => setFilter({...filter, activityStatus: 'dormant'})}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-gray-500"></span>
                No Login 30+ Days
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-600">{partnerStats.no_login_30_days || 0}</div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:bg-red-50 transition-colors" onClick={() => setFilter({...filter, activityStatus: 'never_logged_in'})}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                Never Logged In
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{partnerStats.never_logged_in || 0}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters - Leads */}
      {viewMode === 'leads' && (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {/* Clean Tabs - matching reference design */}
          <div className="flex items-center justify-between border-b bg-white px-4">
            <div className="flex items-center">
              <button 
                onClick={() => { setLeadsTab('all'); setPage(1); }}
                className={`px-5 py-4 font-medium text-base transition-colors border-b-2 -mb-[1px] ${
                  leadsTab === 'all' 
                    ? 'border-gray-800 text-gray-900' 
                    : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
              >
                All Leads
              </button>
              <button 
                onClick={() => { setLeadsTab('overdue'); setPage(1); }}
                className={`px-5 py-4 font-medium text-base transition-colors border-b-2 -mb-[1px] ${
                  leadsTab === 'overdue' 
                    ? 'border-gray-800 text-gray-900' 
                    : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
              >
                Overdue
              </button>
              <button 
                onClick={() => { setLeadsTab('today'); setPage(1); }}
                className={`px-5 py-4 font-medium text-base transition-colors border-b-2 -mb-[1px] ${
                  leadsTab === 'today' 
                    ? 'border-gray-800 text-gray-900' 
                    : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
              >
                Today's Follow-Ups
              </button>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setAddLeadDialogOpen(true)}
              className="text-gray-500 hover:text-gray-700"
            >
              <Plus className="h-5 w-5" />
            </Button>
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap gap-3 items-center p-4 bg-gray-50/50 border-b">
            <Select value={filter.status || 'all'} onValueChange={(v) => setFilter({...filter, status: v === 'all' ? '' : v})}>
              <SelectTrigger className="w-36 h-9 bg-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="in_follow_up">In Follow-Up</SelectItem>
                <SelectItem value="call_back_later">Call Back Later</SelectItem>
                <SelectItem value="did_not_pick_up">Did Not Pick Up</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="interested">Interested</SelectItem>
                <SelectItem value="negotiating">Negotiating</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="closed_referring">Closed & Referring</SelectItem>
                <SelectItem value="needs_reengagement">Needs Re-engagement</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filter.category || 'all'} onValueChange={(v) => setFilter({...filter, category: v === 'all' ? '' : v})}>
              <SelectTrigger className="w-36 h-9 bg-white">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="nutritionist">Nutritionist</SelectItem>
                <SelectItem value="dietician">Dietician</SelectItem>
                <SelectItem value="doctor">Doctor</SelectItem>
                <SelectItem value="lifestyle_coach">Lifestyle Coach</SelectItem>
                <SelectItem value="influencer">Influencer</SelectItem>
                <SelectItem value="fitness_coach">Fitness Coach</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filter.partnerType || 'all'} onValueChange={(v) => setFilter({...filter, partnerType: v === 'all' ? '' : v})}>
              <SelectTrigger className="w-36 h-9 bg-white">
                <SelectValue placeholder="Partner Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Partner Types</SelectItem>
                {partnerTypeOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex-1 min-w-[180px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-9 bg-white"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Filters - Partners */}
      {viewMode === 'partners' && (
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-center">
            <Select value={filter.activityStatus || 'all'} onValueChange={(v) => setFilter({...filter, activityStatus: v === 'all' ? '' : v})}>
              <SelectTrigger className="w-52" data-testid="engagement-status-filter">
                <SelectValue placeholder="Partner Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Partners</SelectItem>
                <SelectItem value="has_transactions">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    Referrals Transacting
                  </div>
                </SelectItem>
                <SelectItem value="added_referrals">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                    Referrals Added
                  </div>
                </SelectItem>
                <SelectItem value="logged_in_only">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                    No Referrals Added
                  </div>
                </SelectItem>
                <SelectItem value="dormant">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-gray-500"></span>
                    No Login (30+ days)
                  </div>
                </SelectItem>
                <SelectItem value="never_logged_in">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                    Never Logged In
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy || 'default'} onValueChange={(v) => setSortBy(v === 'default' ? '' : v)}>
              <SelectTrigger className="w-48" data-testid="sort-by-select">
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Sort: Newest First</SelectItem>
                <SelectItem value="follow_up">Sort: Follow-up Date</SelectItem>
                <SelectItem value="revenue">Sort: Highest Revenue</SelectItem>
                <SelectItem value="referrals">Sort: Most Referrals</SelectItem>
              </SelectContent>
            </Select>

            <Select value={timePeriod} onValueChange={(v) => setTimePeriod(v)}>
              <SelectTrigger className="w-40" data-testid="time-period-select">
                <SelectValue placeholder="Time Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="mtd">Month to Date</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="last_month">Last Month</SelectItem>
                <SelectItem value="month">Last 30 Days</SelectItem>
                <SelectItem value="quarter">Last 90 Days</SelectItem>
                <SelectItem value="year">Last Year</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
            {timePeriod === 'custom' && (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="w-[140px]"
                  data-testid="crm-export-custom-start"
                />
                <span className="text-muted-foreground text-sm">to</span>
                <Input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-[140px]"
                  data-testid="crm-export-custom-end"
                />
              </div>
            )}
            
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or city..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
          {(filter.activityStatus || sortBy || (timePeriod && timePeriod !== 'all')) && (
            <p className="text-sm text-muted-foreground mt-3">
              {filter.activityStatus && `Showing ${
                filter.activityStatus === 'never_logged_in' ? 'Never Logged In' :
                filter.activityStatus === 'logged_in_only' ? 'No Referrals Added' :
                filter.activityStatus === 'added_referrals' ? 'Referrals Added' :
                filter.activityStatus === 'has_transactions' ? 'Referrals Transacting' :
                filter.activityStatus === 'dormant' ? 'No Login (30+ days)' :
                filter.activityStatus} partners`}
              {filter.activityStatus && (sortBy || (timePeriod && timePeriod !== 'all')) && ', '}
              {sortBy && `sorted by ${sortBy === 'revenue' ? 'revenue' : sortBy === 'referrals' ? 'referrals' : sortBy === 'follow_up' ? 'follow-up date' : 'join date'}`}
              {timePeriod && timePeriod !== 'all' && ` for ${timePeriod === 'month' ? 'last 30 days' : timePeriod === 'quarter' ? 'last 90 days' : 'last year'}`}
            </p>
          )}
        </CardContent>
      </Card>
      )}

      {/* Leads Table */}
      {viewMode === 'leads' && (loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : filteredLeads.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold mb-2 text-gray-700">No Leads Found</h3>
            <p className="text-gray-500 mb-4">
              {stats?.total === 0 ? "Click '+' to add your first lead" : "Try adjusting your filters"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-white border-b">
                  <th className="text-left px-4 py-3 font-medium text-gray-700 text-sm">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 text-sm">Phone Number</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 text-sm">Description</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 text-sm">Next Follow-Up Date</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 text-sm">KB Rep</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 text-sm">Remarks</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 text-sm">Type of Partner</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead, index) => (
                  <tr 
                    key={lead.lead_id} 
                    className="border-b hover:bg-blue-50/30 cursor-pointer transition-colors bg-white"
                    onClick={() => { setSelectedLead(lead); setDialogOpen(true); }}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{lead.name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-600">{lead.phone || '-'}</p>
                    </td>
                    <td className="px-4 py-3 max-w-[280px]">
                      <div className="space-y-1">
                        {lead.description && (
                          <div>
                            <p className="text-xs text-gray-400">Latest conversations</p>
                            <p className="text-sm text-gray-700 line-clamp-1">{lead.description}</p>
                          </div>
                        )}
                        {lead.notes && (
                          <div>
                            <p className="text-xs text-gray-400">Earlier conversations</p>
                            <p className="text-sm text-gray-500 line-clamp-1">{lead.notes}</p>
                          </div>
                        )}
                        {!lead.description && !lead.notes && (
                          <p className="text-sm text-gray-300">-</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-600">{lead.follow_up_date || 'N/A'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-600 text-sm">{lead.assigned_rep || '-'}</p>
                    </td>
                    <td className="px-4 py-3">
                      {lead.remarks || lead.status === 'call_back_later' ? (
                        <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                          {lead.remarks || 'Call Back Later'}
                        </span>
                      ) : (
                        <p className="text-gray-300">-</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-600">{lead.partner_type || 'Individual'}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      {/* Partners Grid */}
      {viewMode === 'partners' && (loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : filteredPartners.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold mb-2 text-gray-700">No Partners Found</h3>
            <p className="text-gray-500 mb-4">
              Try adjusting your filters or search
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-white border-b">
                  <th className="text-left px-4 py-3 font-medium text-gray-700 text-sm">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 text-sm">Phone Number</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 text-sm">Follow-Up Date</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 text-sm">Referrals Added</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 text-sm">Revenue Generated</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 text-sm">Commission Earned</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 text-sm">Tier</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 text-sm">
                    <div className="flex items-center gap-1">
                      Engagement
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="p-0.5 rounded hover:bg-gray-100">
                            <Info className="h-3.5 w-3.5 text-gray-400" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-3" align="start">
                          <h4 className="font-semibold text-sm mb-2">Partner Engagement Funnel</h4>
                          <div className="space-y-2 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                              <span><strong>No Login</strong> - Never logged into portal</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full bg-orange-500"></span>
                              <span><strong>No Refs</strong> - Logged in, no referrals added</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500"></span>
                              <span><strong>Has Refs</strong> - Added referrals, none transacting</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                              <span><strong>Transacting</strong> - Has referrals with orders</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full bg-gray-500"></span>
                              <span><strong>Dormant</strong> - No login in 30+ days</span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-2 pt-2 border-t">Click 🕐 to set a follow-up for inactive partners</p>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredPartners.map((partner) => (
                  <tr 
                    key={partner.user_id} 
                    className="border-b hover:bg-blue-50/30 cursor-pointer transition-colors bg-white"
                    onClick={() => { setSelectedPartner(partner); setPartnerDialogOpen(true); }}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {partner.picture ? (
                          <img src={partner.picture} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-4 w-4 text-primary" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-800">{partner.name}</p>
                          <p className="text-xs text-gray-500">{partner.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-600">{partner.phone || '-'}</p>
                    </td>
                    <td className="px-4 py-3">
                      {partner.crm_follow_up_date ? (
                        <div>
                          <p className={`font-medium ${
                            new Date(partner.crm_follow_up_date) < new Date() ? 'text-red-600' :
                            new Date(partner.crm_follow_up_date).toDateString() === new Date().toDateString() ? 'text-blue-600' :
                            'text-gray-700'
                          }`}>
                            {partner.crm_follow_up_date}
                          </p>
                          {new Date(partner.crm_follow_up_date) < new Date() && (
                            <p className="text-xs text-red-500">Overdue</p>
                          )}
                          {new Date(partner.crm_follow_up_date).toDateString() === new Date().toDateString() && (
                            <p className="text-xs text-blue-500">Today</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-400">Not set</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-700 font-medium">{partner.referral_count || 0}</p>
                      {timePeriod && timePeriod !== 'all' && partner.total_referrals !== partner.referral_count && (
                        <p className="text-xs text-gray-400">({partner.total_referrals || 0} total)</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-green-600 font-medium">₹{(partner.total_revenue || 0).toLocaleString()}</p>
                      {timePeriod && timePeriod !== 'all' && partner.period_revenue !== undefined && (
                        <p className="text-xs text-gray-400">₹{(partner.period_revenue || 0).toLocaleString()} ({timePeriod === 'month' ? '30d' : timePeriod === 'quarter' ? '90d' : '1y'})</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-amber-600 font-medium">₹{(partner.total_commission || 0).toLocaleString()}</p>
                      {partner.pending_commission > 0 && (
                        <p className="text-xs text-orange-500">₹{partner.pending_commission.toLocaleString()} unpaid</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                        partner.tier === 'platinum' ? 'bg-purple-100 text-purple-700' :
                        partner.tier === 'gold' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {partner.tier || 'Silver'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${
                          partner.engagement_color === 'red' ? 'bg-red-500' :
                          partner.engagement_color === 'orange' ? 'bg-orange-500' :
                          partner.engagement_color === 'yellow' ? 'bg-yellow-500' :
                          partner.engagement_color === 'green' ? 'bg-green-500' :
                          'bg-gray-500'
                        }`} title={partner.engagement_label || 'Unknown'}></span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          partner.engagement_status === 'has_transactions' ? 'bg-green-100 text-green-700' :
                          partner.engagement_status === 'added_referrals' ? 'bg-yellow-100 text-yellow-700' :
                          partner.engagement_status === 'logged_in_only' ? 'bg-orange-100 text-orange-700' :
                          partner.engagement_status === 'dormant' ? 'bg-gray-100 text-gray-700' :
                          partner.engagement_status === 'never_logged_in' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {partner.engagement_status === 'never_logged_in' ? 'Never Logged In' :
                           partner.engagement_status === 'logged_in_only' ? 'No Referrals' :
                           partner.engagement_status === 'added_referrals' ? 'Referrals Added' :
                           partner.engagement_status === 'has_transactions' ? 'Referrals Transacting' :
                           partner.engagement_status === 'dormant' ? 'No Login 30+ Days' : 'Unknown'}
                        </span>
                        {(partner.engagement_status === 'never_logged_in' || partner.engagement_status === 'dormant') && !partner.crm_follow_up_date && (
                          <button
                            onClick={(e) => setQuickFollowUp(e, partner, 1)}
                            className="ml-1 p-1 rounded hover:bg-orange-100 text-orange-600 transition-colors"
                            title="Set follow-up for tomorrow"
                            data-testid={`quick-followup-${partner.user_id}`}
                          >
                            <Clock className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      {/* Lead Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
            <DialogDescription>View and update lead information</DialogDescription>
          </DialogHeader>
          
          {selectedLead && (
            <div className="space-y-6">
              {/* Edit Button at top */}
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setDialogOpen(false);
                    openEditDialog(selectedLead);
                  }}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit All Details
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Name</Label>
                  <p className="font-semibold">{selectedLead.name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <Select 
                    value={selectedLead.status} 
                    onValueChange={(v) => updateLead(selectedLead.lead_id, { status: v }, false, true)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="in_follow_up">In Follow-Up</SelectItem>
                      <SelectItem value="call_back_later">Call Back Later</SelectItem>
                      <SelectItem value="did_not_pick_up">Did Not Pick Up</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="interested">Interested</SelectItem>
                      <SelectItem value="negotiating">Negotiating</SelectItem>
                      <SelectItem value="converted">Converted</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="closed_referring">Closed & Referring</SelectItem>
                      <SelectItem value="needs_reengagement">Needs Re-engagement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-muted-foreground">Instagram</Label>
                  <div className="flex items-center gap-2">
                    <p>{selectedLead.instagram_handle || 'N/A'}</p>
                    {selectedLead.instagram_handle && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        data-testid="dialog-instagram-btn"
                        onClick={() => {
                          const handle = selectedLead.instagram_handle.replace(/^@/, '').trim();
                          window.open(`https://www.instagram.com/${handle}/`, '_blank', 'noopener,noreferrer');
                        }}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Followers</Label>
                  <p>{formatFollowers(selectedLead.followers)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Category</Label>
                  <p>{getCategoryLabel(selectedLead.category)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">City</Label>
                  <p>{selectedLead.city || 'N/A'}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Personality Types (Select 2)</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {PERSONALITY_TYPES.map(({ value, label }) => {
                      const isSelected = (selectedLead.personality_types || []).includes(value);
                      return (
                        <Button
                          key={value}
                          variant={isSelected ? "default" : "outline"}
                          size="sm"
                          className={isSelected ? getPersonalityColor(value) : ''}
                          onClick={() => togglePersonalityType(value)}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Selected: {(selectedLead.personality_types || []).length}/2
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Contact Attempts</Label>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="h-8 w-8 p-0"
                      disabled={(selectedLead.contact_attempts || 0) === 0}
                      onClick={() => updateLead(selectedLead.lead_id, { contact_attempts: Math.max(0, (selectedLead.contact_attempts || 0) - 1) })}
                    >
                      -
                    </Button>
                    <span className="font-semibold w-8 text-center">{selectedLead.contact_attempts || 0}</span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => updateLead(selectedLead.lead_id, { contact_attempts: (selectedLead.contact_attempts || 0) + 1 })}
                    >
                      +
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Follow-Up Date</Label>
                  <Input
                    type="date"
                    value={selectedLead.follow_up_date || ''}
                    onChange={(e) => updateLead(selectedLead.lead_id, { follow_up_date: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div>
                  <Label className="text-muted-foreground">Assigned KB Rep</Label>
                  <Input
                    defaultValue={selectedLead.assigned_rep || ''}
                    onBlur={(e) => {
                      if (e.target.value !== (selectedLead.assigned_rep || '')) {
                        updateLead(selectedLead.lead_id, { assigned_rep: e.target.value });
                      }
                    }}
                    placeholder="Enter rep name..."
                    className="w-full"
                  />
                </div>
                {selectedLead.email && (
                  <div>
                    <Label className="text-muted-foreground">Email</Label>
                    <p>{selectedLead.email}</p>
                  </div>
                )}
                {selectedLead.phone && (
                  <div>
                    <Label className="text-muted-foreground">Phone</Label>
                    <p>{selectedLead.phone}</p>
                  </div>
                )}
              </div>
              
              {/* URL Analyzer Section */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <Label className="text-blue-800 font-medium">📊 Profile Analyzer (AI)</Label>
                <p className="text-xs text-blue-600 mb-2">Paste Instagram/LinkedIn URL to generate call notes</p>
                <div className="flex gap-2">
                  <Input
                    value={profileUrl}
                    onChange={(e) => setProfileUrl(e.target.value)}
                    placeholder="https://instagram.com/username or linkedin.com/in/..."
                    className="flex-1"
                  />
                  <Button 
                    onClick={() => analyzeProfile(profileUrl, selectedLead.lead_id)}
                    disabled={analyzingProfile || !profileUrl.trim()}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {analyzingProfile ? 'Analyzing...' : 'Analyze'}
                  </Button>
                </div>
              </div>
              
              <div>
                <Label className="text-muted-foreground">Notes</Label>
                <pre className="text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded border max-h-64 overflow-y-auto">{selectedLead.notes || 'No notes'}</pre>
              </div>
              
              <div>
                <Label>Add Note / Follow-up</Label>
                <Textarea 
                  placeholder="Add notes about this lead..."
                  className="mt-2"
                  id="lead-notes"
                />
                <div className="flex gap-2 mt-2">
                  <Button 
                    variant="outline"
                    onClick={() => {
                      const notes = document.getElementById('lead-notes').value;
                      if (notes) {
                        updateLead(selectedLead.lead_id, { 
                          notes: selectedLead.notes ? `${selectedLead.notes}\n\n${new Date().toLocaleDateString()}: ${notes}` : notes 
                        });
                      }
                    }}
                  >
                    Save Note
                  </Button>
                  <Input 
                    type="date" 
                    id="follow-up-date"
                    className="w-48"
                  />
                  <Button 
                    variant="outline"
                    onClick={() => {
                      const date = document.getElementById('follow-up-date').value;
                      if (date) {
                        updateLead(selectedLead.lead_id, { follow_up_date: date });
                        toast.success(`Follow-up set for ${date}`);
                      }
                    }}
                  >
                    Set Follow-up
                  </Button>
                </div>
              </div>
              
              <div className="flex justify-between pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Created: {new Date(selectedLead.created_at).toLocaleDateString()}
                  {selectedLead.contacted_at && (
                    <span> • First contacted: {new Date(selectedLead.contacted_at).toLocaleDateString()}</span>
                  )}
                  {selectedLead.last_contacted_date && (
                    <span> • Last contacted: {new Date(selectedLead.last_contacted_date).toLocaleDateString()}</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => updateLead(selectedLead.lead_id, { status: 'rejected' }, false, true)}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                  <Button 
                    size="sm"
                    disabled={!selectedLead.email}
                    onClick={async () => {
                      if (!selectedLead.email) {
                        toast.error('Lead must have an email to convert to partner');
                        return;
                      }
                      try {
                        // Use XHR to bypass Emergent platform script interception
                        const response = await apiRequest('POST', `${API}/admin/crm/leads/${selectedLead.lead_id}/convert-to-partner`);
                        
                        if (response.ok) {
                          toast.success(`Partner created! Referral code: ${response.data.referral_code}. Welcome email sent.`);
                          setDialogOpen(false);
                          fetchLeads();
                          fetchStats();
                        } else {
                          toast.error(response.data?.detail || `Failed to convert (${response.status})`);
                        }
                      } catch (error) {
                        console.error('Convert error:', error);
                        toast.error('Network error - please check your connection');
                      }
                    }}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Convert to Partner
                  </Button>
                  <Button 
                    variant="destructive"
                    size="sm"
                    onClick={async () => {
                      if (!window.confirm(`Are you sure you want to DELETE lead "${selectedLead.name}"? This cannot be undone!`)) {
                        return;
                      }
                      try {
                        const response = await fetch(`${API}/admin/crm/leads/${selectedLead.lead_id}`, {
                          method: 'DELETE',
                          headers: getAuthHeaders()
                        });
                        const data = await response.json();
                        if (response.ok) {
                          toast.success(data.message);
                          setDialogOpen(false);
                          fetchLeads();
                          fetchStats();
                        } else {
                          toast.error(data.detail || 'Failed to delete lead');
                        }
                      } catch (error) {
                        toast.error('Failed to delete lead');
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Lead
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Partner Detail Dialog */}
      <Dialog open={partnerDialogOpen} onOpenChange={setPartnerDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Partner Details</DialogTitle>
            <DialogDescription>View and manage partner information</DialogDescription>
          </DialogHeader>
          
          {selectedPartner && (
            <div className="space-y-6">
              {/* Header with avatar and key stats */}
              <div className="flex items-center gap-4 pb-4 border-b">
                {selectedPartner.picture ? (
                  <img src={selectedPartner.picture} alt="" className="w-16 h-16 rounded-full" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-8 w-8 text-primary" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-xl font-semibold">{selectedPartner.name}</h3>
                  <p className="text-muted-foreground">{selectedPartner.email}</p>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    <Badge>{selectedPartner.tier || 'Silver'}</Badge>
                    <Badge variant="outline">{selectedPartner.referral_count || 0} referrals</Badge>
                    <Badge className="bg-green-100 text-green-700">₹{(selectedPartner.total_commission || 0).toLocaleString()} earned</Badge>
                    <Badge className="bg-blue-100 text-blue-700">₹{(selectedPartner.total_revenue || 0).toLocaleString()} revenue</Badge>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setAddReferralPartner(selectedPartner)}
                  data-testid={`crm-add-referral-${selectedPartner.user_id}`}
                  title="Add a single referral for this partner"
                >
                  <UserPlus className="h-4 w-4 mr-1" />
                  Add Referral
                </Button>
              </div>

              {/* Editable fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Name</Label>
                  <Input 
                    defaultValue={selectedPartner.name || ''}
                    onBlur={(e) => {
                      if (e.target.value !== selectedPartner.name) {
                        updatePartner(selectedPartner.user_id, { name: e.target.value });
                      }
                    }}
                  />
                </div>
                <div>
                  <Label className="text-muted-foreground">CRM Status</Label>
                  <Select 
                    value={selectedPartner.crm_status || 'active'} 
                    onValueChange={(v) => updatePartner(selectedPartner.user_id, { crm_status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="needs_attention">Needs Attention</SelectItem>
                      <SelectItem value="follow_up">Follow-up Set</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-muted-foreground">Phone</Label>
                  <Input 
                    defaultValue={selectedPartner.phone || ''}
                    onBlur={(e) => {
                      if (e.target.value !== selectedPartner.phone) {
                        updatePartner(selectedPartner.user_id, { phone: e.target.value });
                      }
                    }}
                  />
                </div>
                <div>
                  <Label className="text-muted-foreground">City</Label>
                  <Input 
                    defaultValue={selectedPartner.city || ''}
                    onBlur={(e) => {
                      if (e.target.value !== selectedPartner.city) {
                        updatePartner(selectedPartner.user_id, { city: e.target.value });
                      }
                    }}
                  />
                </div>
                <div>
                  <Label className="text-muted-foreground">Instagram Handle</Label>
                  <Input 
                    defaultValue={selectedPartner.instagram_handle || ''}
                    placeholder="@handle"
                    onBlur={(e) => {
                      if (e.target.value !== selectedPartner.instagram_handle) {
                        updatePartner(selectedPartner.user_id, { instagram_handle: e.target.value });
                      }
                    }}
                  />
                </div>
                <div>
                  <Label className="text-muted-foreground">Referral/Discount Code</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="font-mono text-lg py-2 flex-1">{selectedPartner.referral_code || 'N/A'}</p>
                    {(adminRole === 'super_admin' || adminRole === 'admin') && (
                      <Button
                        variant="default"
                        size="sm"
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        data-testid="edit-discount-code-button"
                        onClick={() => {
                          const newCode = prompt(
                            `Enter new discount code for ${selectedPartner.name}:\n\nCurrent: ${selectedPartner.referral_code || 'None'}\n\nRules:\n- 3-20 characters\n- Letters, numbers, underscores, hyphens only\n- Will be converted to UPPERCASE`,
                            selectedPartner.referral_code || ''
                          );
                          if (newCode && newCode.trim() !== selectedPartner.referral_code) {
                            const xhr = new XMLHttpRequest();
                            xhr.open('PUT', `${API}/admin/partners/${selectedPartner.user_id}/discount-code`, true);
                            xhr.setRequestHeader('Content-Type', 'application/json');
                            xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('session_token') || sessionStorage.getItem('session_token')}`);
                            xhr.onload = function() {
                              if (xhr.status >= 200 && xhr.status < 300) {
                                const response = JSON.parse(xhr.responseText);
                                toast.success(`Discount code updated: ${response.old_code} → ${response.new_code}`);
                                setSelectedPartner(prev => ({ ...prev, referral_code: response.new_code }));
                                fetchPartners();
                              } else {
                                try {
                                  const error = JSON.parse(xhr.responseText);
                                  toast.error(error.detail || 'Failed to update discount code');
                                } catch {
                                  toast.error('Failed to update discount code');
                                }
                              }
                            };
                            xhr.onerror = function() {
                              toast.error('Network error updating discount code');
                            };
                            xhr.send(JSON.stringify({ new_code: newCode.trim() }));
                          }
                        }}
                      >
                        ✏️ Edit Code
                      </Button>
                    )}
                    {(adminRole === 'super_admin' || adminRole === 'admin') && selectedPartner.referral_code && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-orange-400 text-orange-700 hover:bg-orange-50"
                        data-testid="edit-discount-percentage-button"
                        onClick={() => {
                          const input = prompt(
                            `Set discount % for code ${selectedPartner.referral_code}:\n\nThis updates the existing Shopify price rule (no new code created).\nPast orders stay attributed; only the discount amount changes going forward.\n\nEnter a number between 1 and 100 (e.g., 12 for 12%):`,
                            '10'
                          );
                          if (!input) return;
                          const pct = parseFloat(input);
                          if (isNaN(pct) || pct <= 0 || pct > 100) {
                            toast.error('Enter a valid percentage between 1 and 100');
                            return;
                          }
                          const xhr = new XMLHttpRequest();
                          xhr.open('POST', `${API}/admin/shopify/update-discount-percentage`, true);
                          xhr.setRequestHeader('Content-Type', 'application/json');
                          xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('session_token') || sessionStorage.getItem('session_token')}`);
                          xhr.onload = function() {
                            if (xhr.status >= 200 && xhr.status < 300) {
                              const r = JSON.parse(xhr.responseText);
                              toast.success(`Updated: ${r.referral_code} → ${r.discount_percentage}% off on Shopify`);
                            } else {
                              try {
                                const e = JSON.parse(xhr.responseText);
                                toast.error(e.detail || 'Failed to update percentage');
                              } catch {
                                toast.error('Failed to update percentage');
                              }
                            }
                          };
                          xhr.onerror = function() { toast.error('Network error'); };
                          xhr.send(JSON.stringify({ code: selectedPartner.referral_code, percentage: pct }));
                        }}
                      >
                        % Edit %
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Referral Link: <span className="font-mono">kilobeaters-in.myshopify.com/discount/{selectedPartner.referral_code}</span>
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Contact Attempts</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="h-8 w-8 p-0"
                      disabled={(selectedPartner.crm_contact_attempts || 0) === 0}
                      onClick={() => updatePartner(selectedPartner.user_id, { 
                        crm_contact_attempts: Math.max(0, (selectedPartner.crm_contact_attempts || 0) - 1) 
                      })}
                    >
                      -
                    </Button>
                    <span className="font-semibold w-8 text-center">{selectedPartner.crm_contact_attempts || 0}</span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => updatePartner(selectedPartner.user_id, { 
                        crm_contact_attempts: (selectedPartner.crm_contact_attempts || 0) + 1 
                      })}
                    >
                      +
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Follow-Up Date</Label>
                  <Input
                    type="date"
                    value={selectedPartner.crm_follow_up_date || ''}
                    onChange={(e) => updatePartner(selectedPartner.user_id, { crm_follow_up_date: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">Personality Types (Select 2)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {PERSONALITY_TYPES.map(({ value, label }) => {
                    const isSelected = (selectedPartner.crm_personality_types || []).includes(value);
                    return (
                      <Button
                        key={value}
                        variant={isSelected ? "default" : "outline"}
                        size="sm"
                        className={isSelected ? getPersonalityColor(value) : ''}
                        onClick={() => togglePartnerPersonalityType(value)}
                      >
                        {label}
                      </Button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Selected: {(selectedPartner.crm_personality_types || []).length}/2
                </p>
              </div>
              
              <div>
                <Label className="text-muted-foreground">Notes</Label>
                <p className="text-sm bg-gray-50 p-3 rounded-md min-h-[60px]">{selectedPartner.crm_notes || 'No notes yet'}</p>
              </div>
              
              <div>
                <Label>Add Note / Follow-up</Label>
                <Textarea 
                  placeholder="Add notes about this partner..."
                  className="mt-2"
                  id="partner-notes"
                />
                <div className="flex gap-2 mt-2">
                  <Button 
                    variant="outline"
                    onClick={() => {
                      const notes = document.getElementById('partner-notes').value;
                      if (notes) {
                        updatePartner(selectedPartner.user_id, { 
                          crm_notes: selectedPartner.crm_notes ? `${selectedPartner.crm_notes}\n\n${new Date().toLocaleDateString()}: ${notes}` : notes 
                        });
                        document.getElementById('partner-notes').value = '';
                      }
                    }}
                  >
                    Save Note
                  </Button>
                  <Input 
                    type="date" 
                    id="partner-follow-up-date"
                    className="w-48"
                  />
                  <Button 
                    variant="outline"
                    onClick={() => {
                      const date = document.getElementById('partner-follow-up-date').value;
                      if (date) {
                        updatePartner(selectedPartner.user_id, { 
                          crm_follow_up_date: date,
                          crm_status: 'follow_up'
                        });
                        toast.success(`Follow-up set for ${date}`);
                      }
                    }}
                  >
                    Set Follow-up
                  </Button>
                </div>
              </div>
              
              <div className="flex justify-between pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Partner since: {selectedPartner.created_at ? new Date(selectedPartner.created_at).toLocaleDateString() : 'N/A'}
                  {selectedPartner.crm_last_contacted && (
                    <span> • Last follow-up: {new Date(selectedPartner.crm_last_contacted).toLocaleDateString()}</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="destructive"
                    size="sm"
                    onClick={async () => {
                      if (!window.confirm(`Are you sure you want to DELETE ${selectedPartner.name}? Their transactions and payouts will be removed, but referrals will be kept. This cannot be undone!`)) {
                        return;
                      }
                      try {
                        const response = await fetch(`${API}/admin/crm/partners/${selectedPartner.user_id}`, {
                          method: 'DELETE',
                          headers: getAuthHeaders()
                        });
                        const data = await response.json();
                        if (response.ok) {
                          toast.success(data.message);
                          setPartnerDialogOpen(false);
                          fetchPartners();
                          fetchPartnerStats();
                        } else {
                          toast.error(data.detail || 'Failed to delete partner');
                        }
                      } catch (error) {
                        toast.error('Failed to delete partner');
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete Partner
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setPartnerDialogOpen(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Lead Dialog */}
      <Dialog open={addLeadDialogOpen} onOpenChange={setAddLeadDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Lead</DialogTitle>
            <DialogDescription>Manually add a potential partner lead</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input 
                  placeholder="Full name"
                  value={newLead.name}
                  onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Instagram Handle</Label>
                <Input 
                  placeholder="@username"
                  value={newLead.instagram_handle}
                  onChange={(e) => setNewLead({ ...newLead, instagram_handle: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  placeholder="email@example.com"
                  value={newLead.email}
                  onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input 
                  placeholder="+91 98765 43210"
                  value={newLead.phone}
                  onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Followers</Label>
                <Input 
                  placeholder="50000"
                  value={newLead.followers}
                  onChange={(e) => setNewLead({ ...newLead, followers: e.target.value })}
                />
              </div>
              <div>
                <Label>City</Label>
                <Input 
                  placeholder="Mumbai"
                  value={newLead.city}
                  onChange={(e) => setNewLead({ ...newLead, city: e.target.value })}
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select 
                  value={newLead.category} 
                  onValueChange={(v) => setNewLead({ ...newLead, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nutritionist">Nutritionist</SelectItem>
                    <SelectItem value="dietician">Dietician</SelectItem>
                    <SelectItem value="doctor">Doctor</SelectItem>
                    <SelectItem value="lifestyle_coach">Lifestyle Coach</SelectItem>
                    <SelectItem value="influencer">Influencer</SelectItem>
                    <SelectItem value="fitness_coach">Fitness Coach</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>YouTube Channel</Label>
                <Input 
                  placeholder="youtube.com/@channel"
                  value={newLead.youtube_channel}
                  onChange={(e) => setNewLead({ ...newLead, youtube_channel: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Personality Types (Select up to 2)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {PERSONALITY_TYPES.map(({ value, label }) => {
                  const isSelected = (newLead.personality_types || []).includes(value);
                  return (
                    <Button
                      key={value}
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      className={isSelected ? getPersonalityColor(value) : ''}
                      onClick={() => toggleNewLeadPersonality(value)}
                    >
                      {label}
                    </Button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Selected: {(newLead.personality_types || []).length}/2
              </p>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea 
                placeholder="Any notes about this lead..."
                value={newLead.notes}
                onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
              />
            </div>

            {/* New Fields Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium mb-3">Additional Information</h4>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Partner Type</Label>
                  <Select 
                    value={newLead.partner_type || ''} 
                    onValueChange={(v) => setNewLead({ ...newLead, partner_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {partnerTypeOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Profession</Label>
                  <Input 
                    placeholder="e.g., Nutritionist, Personal Trainer"
                    value={newLead.profession || ''}
                    onChange={(e) => setNewLead({ ...newLead, profession: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Years of Experience</Label>
                  <Input 
                    type="number"
                    placeholder="e.g., 5"
                    value={newLead.years_of_experience || ''}
                    onChange={(e) => setNewLead({ ...newLead, years_of_experience: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Sample Status</Label>
                  <Select 
                    value={newLead.sample_status || ''} 
                    onValueChange={(v) => setNewLead({ ...newLead, sample_status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {sampleStatusOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-4">
                <Label>Description (Conversation Notes)</Label>
                <Textarea 
                  placeholder="Latest/earlier conversations..."
                  value={newLead.description || ''}
                  onChange={(e) => setNewLead({ ...newLead, description: e.target.value })}
                />
              </div>
              <div className="mt-4">
                <Label>Remarks (Permanent Notes)</Label>
                <Textarea 
                  placeholder="e.g., Vegan, Gut issues, Allergies..."
                  value={newLead.remarks || ''}
                  onChange={(e) => setNewLead({ ...newLead, remarks: e.target.value })}
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setAddLeadDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={createLead}>
                <Plus className="h-4 w-4 mr-2" />
                Add Lead
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Lead Dialog */}
      <Dialog open={editLeadDialogOpen} onOpenChange={setEditLeadDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Lead</DialogTitle>
            <DialogDescription>Update the lead&apos;s information</DialogDescription>
          </DialogHeader>
          {editingLead && (
            <div className="space-y-4">
              {/* Basic Information */}
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Name *</Label>
                  <Input 
                    placeholder="Lead Name"
                    value={editingLead.name || ''}
                    onChange={(e) => setEditingLead({ ...editingLead, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Instagram Handle</Label>
                  <Input 
                    placeholder="@username"
                    value={editingLead.instagram_handle || ''}
                    onChange={(e) => setEditingLead({ ...editingLead, instagram_handle: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    placeholder="email@example.com"
                    value={editingLead.email || ''}
                    onChange={(e) => setEditingLead({ ...editingLead, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input 
                    placeholder="+91 98765 43210"
                    value={editingLead.phone || ''}
                    onChange={(e) => setEditingLead({ ...editingLead, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Followers</Label>
                  <Input 
                    placeholder="50000"
                    value={editingLead.followers || ''}
                    onChange={(e) => setEditingLead({ ...editingLead, followers: e.target.value })}
                  />
                </div>
                <div>
                  <Label>City</Label>
                  <Input 
                    placeholder="Mumbai"
                    value={editingLead.city || ''}
                    onChange={(e) => setEditingLead({ ...editingLead, city: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select 
                    value={editingLead.category || ''} 
                    onValueChange={(v) => setEditingLead({ ...editingLead, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nutritionist">Nutritionist</SelectItem>
                      <SelectItem value="dietician">Dietician</SelectItem>
                      <SelectItem value="doctor">Doctor</SelectItem>
                      <SelectItem value="lifestyle_coach">Lifestyle Coach</SelectItem>
                      <SelectItem value="influencer">Influencer</SelectItem>
                      <SelectItem value="fitness_coach">Fitness Coach</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>YouTube Channel</Label>
                  <Input 
                    placeholder="youtube.com/@channel"
                    value={editingLead.youtube_channel || ''}
                    onChange={(e) => setEditingLead({ ...editingLead, youtube_channel: e.target.value })}
                  />
                </div>
              </div>

              {/* Status and Tracking */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium mb-3">Status & Tracking</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>Status</Label>
                    <Select 
                      value={editingLead.status || 'new'} 
                      onValueChange={(v) => setEditingLead({ ...editingLead, status: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Follow-up Date</Label>
                    <Input 
                      type="date"
                      value={editingLead.follow_up_date || ''}
                      onChange={(e) => setEditingLead({ ...editingLead, follow_up_date: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Personality Types */}
              <div className="border-t pt-4">
                <Label>Personality Types (Select up to 2)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['power', 'significance', 'approval', 'acceptance', 'intelligence', 'pity'].map((type) => (
                    <Badge
                      key={type}
                      className={`cursor-pointer capitalize ${
                        editingLead.personality_types?.includes(type)
                          ? getPersonalityColor(type)
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      onClick={() => toggleEditLeadPersonality(type)}
                    >
                      {type}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Additional Information */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium mb-3">Additional Information</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>Partner Type</Label>
                    <Select 
                      value={editingLead.partner_type || ''} 
                      onValueChange={(v) => setEditingLead({ ...editingLead, partner_type: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {partnerTypeOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Profession</Label>
                    <Input 
                      placeholder="e.g., Nutritionist, Personal Trainer"
                      value={editingLead.profession || ''}
                      onChange={(e) => setEditingLead({ ...editingLead, profession: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Years of Experience</Label>
                    <Input 
                      type="number"
                      placeholder="e.g., 5"
                      value={editingLead.years_of_experience || ''}
                      onChange={(e) => setEditingLead({ ...editingLead, years_of_experience: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Sample Status</Label>
                    <Select 
                      value={editingLead.sample_status || ''} 
                      onValueChange={(v) => setEditingLead({ ...editingLead, sample_status: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {sampleStatusOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              <div className="border-t pt-4">
                <div>
                  <Label>Description (Conversation Notes)</Label>
                  <Textarea 
                    placeholder="Latest/earlier conversations..."
                    value={editingLead.description || ''}
                    onChange={(e) => setEditingLead({ ...editingLead, description: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div className="mt-4">
                  <Label>Remarks (Permanent Notes)</Label>
                  <Textarea 
                    placeholder="e.g., Vegan, Gut issues, Allergies..."
                    value={editingLead.remarks || ''}
                    onChange={(e) => setEditingLead({ ...editingLead, remarks: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div className="mt-4">
                  <Label>General Notes</Label>
                  <Textarea 
                    placeholder="Any other notes..."
                    value={editingLead.notes || ''}
                    onChange={(e) => setEditingLead({ ...editingLead, notes: e.target.value })}
                    className="mt-2"
                  />
                </div>
              </div>

              <DialogFooter className="border-t pt-4">
                <Button variant="outline" onClick={() => { setEditLeadDialogOpen(false); setEditingLead(null); }}>
                  Cancel
                </Button>
                <Button onClick={handleEditLead}>
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Import Results Dialog */}
      <Dialog open={importResultsOpen} onOpenChange={setImportResultsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Excel Import Results
            </DialogTitle>
          </DialogHeader>
          
          {importResults && (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-700">{importResults.imported}</div>
                  <div className="text-sm text-green-600">Imported</div>
                </div>
                <div className={`border rounded-lg p-3 text-center ${importResults.skipped > 0 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className={`text-2xl font-bold ${importResults.skipped > 0 ? 'text-amber-700' : 'text-gray-500'}`}>{importResults.skipped}</div>
                  <div className={`text-sm ${importResults.skipped > 0 ? 'text-amber-600' : 'text-gray-500'}`}>Skipped</div>
                </div>
              </div>
              
              {/* Skipped Details */}
              {importResults.skipped > 0 && importResults.skipped_details && (
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="text-sm font-medium text-gray-700">Skipped Breakdown:</div>
                  <div className="text-sm text-gray-600 space-y-1">
                    {importResults.skipped_details.missing_name > 0 && (
                      <div className="flex justify-between">
                        <span>Missing name (required)</span>
                        <span className="font-medium">{importResults.skipped_details.missing_name}</span>
                      </div>
                    )}
                    {importResults.skipped_details.duplicates > 0 && (
                      <div className="flex justify-between">
                        <span>Duplicate Instagram handles</span>
                        <span className="font-medium">{importResults.skipped_details.duplicates}</span>
                      </div>
                    )}
                    {importResults.skipped_details.empty_rows > 0 && (
                      <div className="flex justify-between text-gray-400">
                        <span>Empty rows (ignored)</span>
                        <span className="font-medium">{importResults.skipped_details.empty_rows}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Error Details */}
              {importResults.errors && importResults.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="text-sm font-medium text-red-700 mb-2">Details (first {importResults.errors.length}):</div>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {importResults.errors.map((error, idx) => (
                      <div key={idx} className="text-xs text-red-600 font-mono">
                        {error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <DialogFooter>
                <Button onClick={() => setImportResultsOpen(false)}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Database Search Dialog */}
      <Dialog open={dbSearchOpen} onOpenChange={setDbSearchOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Database Search</DialogTitle>
            <DialogDescription>Search for email, phone, or name across all collections</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              {(() => {
                const handleDbSearch = async () => {
                  if (!dbSearchQuery || dbSearchQuery.length < 3) {
                    toast.error('Enter at least 3 characters');
                    return;
                  }
                  setDbSearching(true);
                  try {
                    const response = await fetch(`${API}/admin/crm/database-search?query=${encodeURIComponent(dbSearchQuery)}`, {
                      headers: getAuthHeaders()
                    });
                    const data = await response.json();
                    if (response.ok) {
                      setDbSearchResults(data);
                      if (data.total_found === 0) {
                        toast.info('No results found');
                      }
                    } else {
                      toast.error(data.detail || 'Search failed');
                    }
                  } catch (error) {
                    toast.error('Search failed: ' + error.message);
                  } finally {
                    setDbSearching(false);
                  }
                };
                return (
                  <>
                    <Input
                      placeholder="Enter email, phone, or name..."
                      value={dbSearchQuery}
                      onChange={(e) => setDbSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleDbSearch();
                        }
                      }}
                      className="flex-1"
                    />
                    <Button onClick={handleDbSearch} disabled={dbSearching}>
                      {dbSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </>
                );
              })()}
            </div>
            
            {dbSearchResults && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Found {dbSearchResults.total_found} results for "{dbSearchResults.query}"</p>
                
                {dbSearchResults.users.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Users ({dbSearchResults.users.length})</h4>
                    <div className="bg-gray-50 rounded p-2 space-y-1 text-sm">
                      {dbSearchResults.users.map((u, i) => (
                        <div key={i} className="flex justify-between items-center p-2 bg-white rounded">
                          <span>{u.name} - {u.email} - {u.phone} ({u.role})</span>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={async () => {
                              if (!window.confirm(`Delete user ${u.name}?`)) return;
                              const res = await fetch(`${API}/admin/crm/database-delete?collection=users&record_id=${u.user_id}`, {
                                method: 'DELETE',
                                headers: getAuthHeaders()
                              });
                              if (res.ok) {
                                toast.success('Deleted');
                                setDbSearchResults(prev => ({
                                  ...prev,
                                  users: prev.users.filter(x => x.user_id !== u.user_id),
                                  total_found: prev.total_found - 1
                                }));
                              } else {
                                toast.error('Failed to delete');
                              }
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {dbSearchResults.crm_leads.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">CRM Leads ({dbSearchResults.crm_leads.length})</h4>
                    <div className="bg-gray-50 rounded p-2 space-y-1 text-sm">
                      {dbSearchResults.crm_leads.map((l, i) => (
                        <div key={i} className="flex justify-between items-center p-2 bg-white rounded">
                          <span>{l.name} - {l.email} - {l.phone} (status: {l.status})</span>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={async () => {
                              if (!window.confirm(`Delete lead ${l.name}?`)) return;
                              const res = await fetch(`${API}/admin/crm/database-delete?collection=crm_leads&record_id=${l.lead_id}`, {
                                method: 'DELETE',
                                headers: getAuthHeaders()
                              });
                              if (res.ok) {
                                toast.success('Deleted');
                                setDbSearchResults(prev => ({
                                  ...prev,
                                  crm_leads: prev.crm_leads.filter(x => x.lead_id !== l.lead_id),
                                  total_found: prev.total_found - 1
                                }));
                              } else {
                                toast.error('Failed to delete');
                              }
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {dbSearchResults.referrals.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Referrals ({dbSearchResults.referrals.length})</h4>
                    <div className="bg-gray-50 rounded p-2 space-y-1 text-sm">
                      {dbSearchResults.referrals.map((r, i) => (
                        <div key={i} className="flex justify-between items-center p-2 bg-white rounded">
                          <span>{r.name} - {r.email} - {r.phone} (partner: {r.partner_id}, status: {r.status})</span>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={async () => {
                              if (!window.confirm(`Delete referral ${r.name}?`)) return;
                              const res = await fetch(`${API}/admin/crm/database-delete?collection=referrals&record_id=${r.referral_id}`, {
                                method: 'DELETE',
                                headers: getAuthHeaders()
                              });
                              if (res.ok) {
                                toast.success('Deleted');
                                setDbSearchResults(prev => ({
                                  ...prev,
                                  referrals: prev.referrals.filter(x => x.referral_id !== r.referral_id),
                                  total_found: prev.total_found - 1
                                }));
                              } else {
                                toast.error('Failed to delete');
                              }
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {dbSearchResults.shopify_transactions.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Shopify Transactions ({dbSearchResults.shopify_transactions.length})</h4>
                    <div className="bg-gray-50 rounded p-2 space-y-1 text-sm">
                      {dbSearchResults.shopify_transactions.map((t, i) => (
                        <div key={i} className="flex justify-between items-center p-2 bg-white rounded">
                          <span>{t.customer_name} - {t.customer_email} - {t.customer_phone} (order: {t.order_id})</span>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={async () => {
                              if (!window.confirm(`Delete transaction ${t.order_id}?`)) return;
                              const res = await fetch(`${API}/admin/crm/database-delete?collection=shopify_transactions&record_id=${t.order_id}`, {
                                method: 'DELETE',
                                headers: getAuthHeaders()
                              });
                              if (res.ok) {
                                toast.success('Deleted');
                                setDbSearchResults(prev => ({
                                  ...prev,
                                  shopify_transactions: prev.shopify_transactions.filter(x => x.order_id !== t.order_id),
                                  total_found: prev.total_found - 1
                                }));
                              } else {
                                toast.error('Failed to delete');
                              }
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Single-add referral dialog (shared with AdminPartners) */}
      <AddReferralDialog
        open={!!addReferralPartner}
        onOpenChange={(o) => { if (!o) setAddReferralPartner(null); }}
        partner={addReferralPartner}
        onAdded={() => { if (fetchPartners) fetchPartners(); }}
      />
    </div>
  );
}
